import type { BoxProps } from "@mui/material";
import { Box, Button, Grow, Popper, Slide, Stack } from "@mui/material";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import type { FC } from "react";
import { usePopover } from "../../utils/hooks/use-popover";
import type { ContentTone } from "../brand-kit/server/brand-kit-service";
import { useAccountChannels } from "./hooks/use-account-channels";
import { useLaunchComms } from "./hooks/use-launch-comms";

interface GenerateCommsButtonProps extends BoxProps {
  contentTone: ContentTone | "custom";
  description: string;
}

export const GenerateCommsButton: FC<GenerateCommsButtonProps> = ({ contentTone, description }) => {
  const createButtonPopover = usePopover<HTMLButtonElement>();

  const { channels, loading: channelsLoading } = useAccountChannels();
  const { launch, launching, savable } = useLaunchComms(contentTone, description);

  if (channelsLoading) return null;

  return (
    // eslint-disable-next-line react-hooks/refs
    <Box position="relative" onMouseOut={createButtonPopover.handleClose}>
      <Popper
        // eslint-disable-next-line react-hooks/refs
        onMouseOut={createButtonPopover.handleClose}
        // eslint-disable-next-line react-hooks/refs
        onMouseOver={createButtonPopover.handleOpen}
        // eslint-disable-next-line react-hooks/refs
        open={!launching && createButtonPopover.open}
        // eslint-disable-next-line react-hooks/refs
        anchorEl={createButtonPopover.anchorRef.current}
        placement="top-start"
        sx={{ zIndex: 2000 }}
        transition
      >
        {({ TransitionProps }) => (
          <Box
            width={createButtonPopover.anchorRef.current?.offsetWidth}
            display="flex"
            flexDirection="row-reverse"
            borderRadius="10px 10px 0 0"
            bgcolor="primary.light"
            p={2}
            overflow="hidden"
            zIndex={3000}
          >
            <Slide direction="up" in={createButtonPopover.open} mountOnEnter unmountOnExit>
              <Stack direction="column" spacing={1} width="100%">
                {channels.map(({ name, channel_name, icon }, i) => (
                  <Grow {...TransitionProps} timeout={100 * i} key={channel_name}>
                    <Button
                      data-analytics-id={`generate-moment-create-your-moment-${channel_name}`}
                      startIcon={icon}
                      variant="outlined"
                      fullWidth
                      disabled={!savable}
                      sx={{
                        backgroundColor: "#fff",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.8)",
                        },
                      }}
                      onClick={() => savable && void launch(channel_name).catch(console.error)}
                    >
                      {name}
                    </Button>
                  </Grow>
                ))}
              </Stack>
            </Slide>
          </Box>
        )}
      </Popper>
      <Button
        data-analytics-id="generate-moment-create-your-moment"
        loading={launching || !savable}
        endIcon={
          <ArrowRightIcon
            // eslint-disable-next-line react-hooks/refs
            sx={{
              transition: "transform 0.2s ease-in-out",
              // eslint-disable-next-line react-hooks/refs
              ...(createButtonPopover.open && {
                transform: "rotate(-90deg)",
              }),
            }}
          />
        }
        variant="contained"
        sx={(theme) => ({
          position: "relative",
          zIndex: 2,
          cursor: "default",
          ...(createButtonPopover.open && {
            borderTopLeftRadius: "0px",
            borderTopRightRadius: "0px",
            bgcolor: `${theme.palette.primary.light} !important`,
          }),
          transition: "transform 0.2s ease-in-out",
        })}
        // eslint-disable-next-line react-hooks/refs
        onMouseOver={createButtonPopover.handleOpen}
        // eslint-disable-next-line react-hooks/refs
        ref={createButtonPopover.anchorRef}
      >
        Edit this Communication
      </Button>
    </Box>
  );
};
